import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Sparkles } from '@react-three/drei';
import * as THREE from 'three';

// Cache generated supernova skysphere texture
let cachedSupernovaTexture = null;

function generateSupernovaTexture() {
  if (cachedSupernovaTexture) return cachedSupernovaTexture;

  const width = 2048;
  const height = 1024;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  // Deep space base gradient
  const bgGrad = ctx.createLinearGradient(0, 0, 0, height);
  bgGrad.addColorStop(0, '#06070a');
  bgGrad.addColorStop(0.3, '#0b0f19');
  bgGrad.addColorStop(0.5, '#05070c');
  bgGrad.addColorStop(0.8, '#0d091a');
  bgGrad.addColorStop(1, '#05070a');
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, width, height);

  // Helper for fractal noise in canvas
  function snoise(x, y) {
    const n = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453;
    return n - Math.floor(n);
  }

  function smoothNoise(x, y) {
    const i = Math.floor(x);
    const j = Math.floor(y);
    const fx = x - i;
    const fy = y - j;
    const a = snoise(i, j);
    const b = snoise(i + 1, j);
    const c = snoise(i, j + 1);
    const d = snoise(i + 1, j + 1);
    const ux = fx * fx * (3 - 2 * fx);
    const uy = fy * fy * (3 - 2 * fy);
    return a * (1 - ux) * (1 - uy) + b * ux * (1 - uy) + c * (1 - ux) * uy + d * ux * uy;
  }

  function fbm(x, y, octaves = 6) {
    let val = 0;
    let amp = 0.5;
    let freq = 1;
    for (let o = 0; o < octaves; o++) {
      val += smoothNoise(x * freq, y * freq) * amp;
      amp *= 0.5;
      freq *= 2.1;
    }
    return val;
  }

  // Draw 14 large overlapping dense supernova nebula clouds across the sky
  const nebulaClouds = [
    // 1. Great Carina Magenta Shockwave
    { x: 300, y: 350, rx: 420, ry: 260, color1: '#f43f5e', color2: '#881337', color3: '#fda4af', power: 0.85 },
    // 2. Cyan Oxygen-III Ionized Cloud
    { x: 850, y: 600, rx: 480, ry: 290, color1: '#06b6d4', color2: '#083344', color3: '#67e8f9', power: 0.9 },
    // 3. Deep Cosmic Violet Veil
    { x: 1450, y: 320, rx: 540, ry: 320, color1: '#8b5cf6', color2: '#2e1065', color3: '#c084fc', power: 0.85 },
    // 4. Golden Solar Nursery
    { x: 1800, y: 680, rx: 400, ry: 240, color1: '#f59e0b', color2: '#451a03', color3: '#fef08a', power: 0.8 },
    // 5. Emerald Green Gas Belt
    { x: 600, y: 200, rx: 360, ry: 200, color1: '#10b981', color2: '#022c22', color3: '#6ee7b7', power: 0.7 },
    // 6. Crimson Flare Core
    { x: 1150, y: 400, rx: 460, ry: 280, color1: '#e11d48', color2: '#4c0519', color3: '#fb7185', power: 0.85 },
    // 7. Ultramarine High-Energy Cloud
    { x: 200, y: 780, rx: 440, ry: 260, color1: '#3b82f6', color2: '#172554', color3: '#93c5fd', power: 0.8 },
    // 8. Flaming Amber Supernova Core
    { x: 1600, y: 750, rx: 380, ry: 220, color1: '#ea580c', color2: '#431407', color3: '#fdba74', power: 0.85 },
    // 9. Polar Nebula Ring (North)
    { x: 1000, y: 80, rx: 600, ry: 160, color1: '#7c3aed', color2: '#1e1b4b', color3: '#a78bfa', power: 0.75 },
    // 10. Polar Nebula Ring (South)
    { x: 1000, y: 940, rx: 650, ry: 180, color1: '#0284c7', color2: '#082f49', color3: '#38bdf8', power: 0.75 },
    // 11. Center Galactic Ridge
    { x: 1024, y: 512, rx: 900, ry: 220, color1: '#ec4899', color2: '#500724', color3: '#f472b6', power: 0.65 },
  ];

  ctx.globalCompositeOperation = 'screen';

  nebulaClouds.forEach((c) => {
    const radGrad = ctx.createRadialGradient(c.x, c.y, 0, c.x, c.y, Math.max(c.rx, c.ry));
    radGrad.addColorStop(0, c.color3);
    radGrad.addColorStop(0.25, c.color1);
    radGrad.addColorStop(0.65, c.color2);
    radGrad.addColorStop(1, 'rgba(0,0,0,0)');

    ctx.save();
    ctx.translate(c.x, c.y);
    ctx.scale(c.rx / 200, c.ry / 200);
    ctx.fillStyle = radGrad;
    ctx.beginPath();
    ctx.arc(0, 0, 200, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  });

  // Add rich turbulent plasma filaments and cosmic dust texture overlay
  const imgData = ctx.getImageData(0, 0, width, height);
  const data = imgData.data;

  for (let y = 0; y < height; y += 2) {
    for (let x = 0; x < width; x += 2) {
      const idx = (y * width + x) * 4;
      const n = fbm(x / 45, y / 45, 5);

      if (n > 0.45) {
        const factor = (n - 0.45) * 1.8;
        // Inject glowing plasma strands into existing colors
        data[idx] = Math.min(255, data[idx] + factor * 70);
        data[idx + 1] = Math.min(255, data[idx + 1] + factor * 50);
        data[idx + 2] = Math.min(255, data[idx + 2] + factor * 90);
      }
    }
  }
  ctx.putImageData(imgData, 0, 0);

  // Add 1200 bright pinpoint background stars
  ctx.globalCompositeOperation = 'lighter';
  for (let i = 0; i < 1200; i++) {
    const sx = Math.random() * width;
    const sy = Math.random() * height;
    const size = Math.random() < 0.9 ? Math.random() * 1.4 : 1.5 + Math.random() * 2.2;
    const starColor = Math.random() < 0.4 ? '#ffffff' : Math.random() < 0.7 ? '#67e8f9' : '#fde047';

    ctx.fillStyle = starColor;
    ctx.beginPath();
    ctx.arc(sx, sy, size, 0, Math.PI * 2);
    ctx.fill();

    // Occasional bright star with cross diffraction spikes
    if (size > 2.2) {
      ctx.strokeStyle = starColor;
      ctx.lineWidth = 0.75;
      ctx.beginPath();
      ctx.moveTo(sx - size * 4, sy);
      ctx.lineTo(sx + size * 4, sy);
      ctx.moveTo(sx, sy - size * 4);
      ctx.lineTo(sx, sy + size * 4);
      ctx.stroke();
    }
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  cachedSupernovaTexture = texture;
  return texture;
}

export default function SupernovaBackground() {
  const skyDomeRef = useRef();
  const gasPuffRef1 = useRef();
  const gasPuffRef2 = useRef();

  const supernovaTexture = useMemo(() => generateSupernovaTexture(), []);

  useFrame((_, delta) => {
    if (skyDomeRef.current) {
      skyDomeRef.current.rotation.y += delta * 0.006;
      skyDomeRef.current.rotation.x += delta * 0.002;
    }
    if (gasPuffRef1.current) {
      gasPuffRef1.current.rotation.z += delta * 0.015;
    }
    if (gasPuffRef2.current) {
      gasPuffRef2.current.rotation.z -= delta * 0.012;
    }
  });

  return (
    <group>
      {/* ═══════════════════════════════════════════════════════════
          1. 360° SUPERNOVA SKY DOME (Surrounds entire universe)
          ═══════════════════════════════════════════════════════════ */}
      <mesh ref={skyDomeRef} position={[0, 0, 0]}>
        <sphereGeometry args={[420, 64, 64]} />
        <meshBasicMaterial
          map={supernovaTexture}
          side={THREE.BackSide}
          depthWrite={false}
        />
      </mesh>

      {/* ═══════════════════════════════════════════════════════════
          2. 3D VOLUMETRIC SUPERNOVA GAS CLOUDS & FILAMENTS
          ═══════════════════════════════════════════════════════════ */}
      {/* Dense Magenta/Purple Hydrogen Cloud Field */}
      <group ref={gasPuffRef1} position={[0, 0, -40]}>
        <Sparkles
          count={500}
          scale={[180, 120, 300]}
          size={3.8}
          speed={0.3}
          opacity={0.65}
          color="#f43f5e"
        />
        <Sparkles
          count={450}
          scale={[200, 140, 320]}
          size={3.2}
          speed={0.25}
          opacity={0.6}
          color="#a855f7"
        />
      </group>

      {/* Dense Cyan/Azure Oxygen Gas Field */}
      <group ref={gasPuffRef2} position={[0, 0, -80]}>
        <Sparkles
          count={550}
          scale={[220, 150, 340]}
          size={3.5}
          speed={0.35}
          opacity={0.7}
          color="#38bdf8"
        />
        <Sparkles
          count={400}
          scale={[190, 130, 280]}
          size={4.0}
          speed={0.2}
          opacity={0.6}
          color="#43DDD0"
        />
      </group>

      {/* Warm Golden Stellar Nursery Core */}
      <Sparkles
        count={350}
        scale={[160, 100, 260]}
        position={[20, 10, -20]}
        size={3.0}
        speed={0.28}
        opacity={0.55}
        color="#fde047"
      />
    </group>
  );
}
